import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import AppLayout from './layout/AppLayout'
import AuthGuard from './components/AuthGuard'
import Login from './pages/Login'
import VerifyInvoice from './pages/VerifyInvoice'
import Dashboard from './pages/Dashboard'
import Clients from './pages/Clients'
import ClientDetailPage from './pages/ClientDetailPage'
import Cases from './pages/Cases'
import CaseDetailPage from './pages/CaseDetailPage'
import Projects from './pages/Projects'
import ProjectDetailPage from './pages/ProjectDetailPage'
import Documents from './pages/Documents'
import Inbox from './pages/Inbox'
import Marketing from './pages/Marketing'
import MarketingSuppressions from './pages/MarketingSuppressions'
import MarketingCampaigns from './pages/MarketingCampaigns'
import MarketingCampaignDetail from './pages/MarketingCampaignDetail'
import MarketingTemplates from './pages/MarketingTemplates'
import Agenda from './pages/Agenda'
import Tasks from './pages/Tasks'
import Billing from './pages/Billing'
import InvoiceDetailPage from './pages/InvoiceDetailPage'
import NewInvoicePage from './pages/NewInvoicePage'
import Quotes from './pages/Quotes'
import QuoteEditorPage from './pages/QuoteEditorPage'
import Rates from './pages/Rates'
import Expenses from './pages/Expenses'
import ExpenseSheets from './pages/ExpenseSheets'
import AccountingAEAT from './pages/AccountingAEAT'
import AccountingClose from './pages/AccountingClose'
import AccountingAudit from './pages/AccountingAudit'
import AccountingJournal from './pages/AccountingJournal'
import AccountingLedger from './pages/AccountingLedger'
import Banking from './pages/Banking'
import BankingCallback from './pages/BankingCallback'
import FixedAssets from './pages/FixedAssets'
import AccountingAnnual from './pages/AccountingAnnual'
import AccountingBudget from './pages/AccountingBudget'
import AccountingTrialBalance from './pages/AccountingTrialBalance'
import AccountingPnl from './pages/AccountingPnl'
import AccountingBalance from './pages/AccountingBalance'
import AccountingKpis from './pages/AccountingKpis'
import AIAssistant from './pages/AIAssistant'
import OCR from './pages/OCR'
import Settings from './pages/Settings'
import SettingsLayout from './pages/settings/SettingsLayout'
import Profile from './pages/settings/Profile'
import Users from './pages/settings/Users'
import ProjectRoles from './pages/settings/ProjectRoles'
import Products from './pages/settings/Products'
import ServiceLinesSettingsPage from './pages/settings/ServiceLines'
import ExpenseCategoriesSettingsPage from './pages/settings/ExpenseCategories'
import ExpenseRulesSettingsPage from './pages/settings/ExpenseRules'
import CatalogsSettingsPage from './pages/settings/Catalogs'
import Advisors from './pages/settings/Advisors'
import FirmPage from './pages/settings/Firm'
import BillingSettingsPage from './pages/settings/Billing'
import EmailSettingsPage from './pages/settings/Email'
import IntegrationsPage from './pages/settings/Integrations'
import TemplatesSettingsPage from './pages/settings/Templates'
import RecurringSettingsPage from './pages/settings/Recurring'
import SecuritySettingsPage from './pages/settings/Security'

/**
 * Página de inicio según rol: CONTABILIDAD (gestoría) aterriza en Facturación
 * (su dashboard comercial devuelve 403); el resto en el Dashboard.
 */
function HomeRedirect() {
  const auth = useAuth()
  const to =
    auth.status === 'authenticated' && auth.user.role === 'CONTABILIDAD'
      ? '/billing'
      : '/dashboard'
  return <Navigate to={to} replace />
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* PÚBLICA a propósito, fuera de AuthGuard: es el destino del QR impreso
          en las facturas y quien lo escanea es el cliente, sin cuenta en el CRM. */}
      <Route path="/verify" element={<VerifyInvoice />} />

      <Route
        element={
          <AuthGuard>
            <AppLayout />
          </AuthGuard>
        }
      >
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/dashboard" element={<Dashboard />} />

        {/* Gestión */}
        <Route path="/clients" element={<Clients />} />
        <Route path="/clients/:id" element={<ClientDetailPage />} />
        <Route path="/opportunities" element={<Cases />} />
        <Route path="/opportunities/:id" element={<CaseDetailPage />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:id" element={<ProjectDetailPage />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/inbox" element={<Inbox />} />
        {/* Herramienta de marketing PROPIA (en construcción). La de hoy, JR2
            MKT, es otra aplicación y se abre desde el icono de la barra
            superior, no desde aquí. */}
        <Route path="/marketing" element={<Marketing />} />
        <Route path="/marketing/suppressions" element={<MarketingSuppressions />} />
        <Route path="/marketing/campaigns" element={<MarketingCampaigns />} />
        <Route path="/marketing/campaigns/:id" element={<MarketingCampaignDetail />} />
        <Route path="/marketing/templates" element={<MarketingTemplates />} />

        {/* Agenda */}
        <Route path="/agenda" element={<Agenda />} />
        <Route path="/tasks" element={<Tasks />} />

        {/* Administración */}
        <Route path="/quotes" element={<Quotes />} />
        <Route path="/quotes/new" element={<QuoteEditorPage />} />
        <Route path="/quotes/:id" element={<QuoteEditorPage />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/billing/new" element={<NewInvoicePage />} />
        <Route path="/billing/:id" element={<InvoiceDetailPage />} />
        <Route path="/rates" element={<Rates />} />
        <Route path="/expenses" element={<Expenses />} />
        <Route path="/expense-sheets" element={<ExpenseSheets />} />

        {/* Contabilidad */}
        <Route path="/accounting/journal" element={<AccountingJournal />} />
        <Route path="/accounting/ledger" element={<AccountingLedger />} />
        <Route path="/accounting/trial-balance" element={<AccountingTrialBalance />} />
        <Route path="/accounting/pnl" element={<AccountingPnl />} />
        <Route path="/accounting/balance" element={<AccountingBalance />} />
        <Route path="/accounting/aeat" element={<AccountingAEAT />} />
        <Route path="/accounting/kpis" element={<AccountingKpis />} />
        <Route path="/accounting/close" element={<AccountingClose />} />
        <Route path="/accounting/audit" element={<AccountingAudit />} />
        <Route path="/banking" element={<Banking />} />
        {/* Vuelta del banco tras autorizar (PSD2). Dentro del guard: el canje
            exige ADMIN y quien vuelve es quien inició la conexión. */}
        <Route path="/banking/callback" element={<BankingCallback />} />
        <Route path="/fixed-assets" element={<FixedAssets />} />
        <Route path="/accounting/annual" element={<AccountingAnnual />} />
        <Route path="/accounting/budget" element={<AccountingBudget />} />

        {/* IA */}
        <Route path="/ai" element={<AIAssistant />} />
        <Route path="/ocr" element={<OCR />} />

        <Route path="/settings" element={<SettingsLayout />}>
          <Route index element={<Settings />} />
          <Route path="profile" element={<Profile />} />
          <Route path="security" element={<SecuritySettingsPage />} />
          <Route path="users" element={<Users />} />
          <Route path="project-roles" element={<CatalogsSettingsPage />} />
          <Route path="products" element={<CatalogsSettingsPage />} />
          <Route path="service-lines" element={<CatalogsSettingsPage />} />
          <Route path="expense-categories" element={<CatalogsSettingsPage />} />
          <Route path="catalogs" element={<CatalogsSettingsPage />} />
          <Route path="expense-rules" element={<CatalogsSettingsPage />} />
          <Route path="advisors" element={<CatalogsSettingsPage />} />
          <Route path="firm" element={<FirmPage />} />
          <Route path="billing" element={<BillingSettingsPage />} />
          <Route path="email" element={<EmailSettingsPage />} />
          <Route path="integrations" element={<IntegrationsPage />} />
          <Route path="templates" element={<TemplatesSettingsPage />} />
          <Route path="recurring" element={<RecurringSettingsPage />} />
        </Route>
        <Route path="*" element={<HomeRedirect />} />
      </Route>
    </Routes>
  )
}
